import BookingModal from '@/components/BookingModal';
import BookingActions from '@/components/booking/BookingActions';
import PricingBreakdown from '@/components/booking/PricingBreakdown';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import { calculateNights, formatPrice } from '@/utils/bookingUtils';
import { CLEANING_FEE } from '@/utils/constants';
import { getPropertyShortName } from '@/utils/propertyUtils';

import { useSearchParamsState } from '@/hooks/search/useSearchParamsState';
import { useEffect, useState } from 'react';
import BookingSearchForm from './forms/BookingSearchForm';

interface BookingCardProps {
  price: number;
  externalCheckIn?: Date | undefined;
  externalCheckOut?: Date | undefined;
  onDateChange?: (
    checkIn: Date | undefined,
    checkOut: Date | undefined,
  ) => void;
  shouldResetGuests?: boolean;
  onGuestsReset?: () => void;
  maxGuests?: number;
  airbnbUrl?: string;
  propertyTitle: string;
  unavailableDates?: Date[];
  availabilityLoading?: boolean;
  property?: {
    id: string;
    rating: number;
    guests: number;
    reviews?: unknown[];
    images?: string[];
    host?: { name: string };
  };
}

const BookingCard = ({
  price,
  onDateChange,
  maxGuests = 6,
  airbnbUrl,
  propertyTitle,
  availabilityLoading,
  shouldResetGuests,
  onGuestsReset,
  unavailableDates = [],
  property,
}: BookingCardProps) => {
  const { trackRequestReservation } = useAmplitudeEvents();

  const { checkInDate, checkOutDate } = useSearchParamsState(150);
  const fixedGuests = String(property?.guests ?? maxGuests);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const nights =
    checkInDate && checkOutDate
      ? calculateNights(checkInDate, checkOutDate)
      : 0;

  const showPricing = checkInDate && checkOutDate && nights > 0;
  const totalPrice = (nights > 0 ? price * nights : 0) + CLEANING_FEE;

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleBookingClick = () => {
    if (!checkInDate || !checkOutDate) return;

    const propertyShortName = getPropertyShortName(propertyTitle);
    const totalReviews = property?.reviews?.length || 0;
    const totalPhotos = property?.images?.length || 0;
    const hostName = property?.host?.name || '';

    trackRequestReservation(
      checkInDate,
      checkOutDate,
      fixedGuests,
      propertyTitle,
      propertyShortName,
      property?.rating || 0,
      property?.guests || maxGuests,
      price,
      totalReviews,
      totalPhotos,
      hostName,
    );

    setIsModalOpen(true);
  };

  useEffect(() => {
    if (shouldResetGuests) {
      onGuestsReset?.();
    }
  }, [shouldResetGuests, onGuestsReset]);

  return (
    <>
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 p-6 shadow-lg sticky top-24">
        <div className="mb-6">
          <div className="flex items-baseline space-x-1">
            <span className="text-2xl font-semibold text-gray-900">
              {formatPrice(price)}
            </span>
            <span className="text-gray-600">noche</span>
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Costo de limpieza: {formatPrice(CLEANING_FEE)}
          </div>
        </div>

        <BookingSearchForm
          variant="desktop"
          showTitle={false}
          initialCheckIn={checkInDate ?? undefined}
          initialCheckOut={checkOutDate ?? undefined}
          initialGuests={fixedGuests}
          maxGuests={property?.guests ?? maxGuests}
          unavailableDates={unavailableDates}
          onDateChange={onDateChange}
          guestsDisabled={true}
          showSubmit={false}
          syncOnChange={true}
        />

        {availabilityLoading && (
          <div className="mt-4 text-sm text-brand-primary">
            Actualizando disponibilidad...
          </div>
        )}

        {showPricing && (
          <PricingBreakdown
            nights={nights}
            price={price}
            cleaningFee={CLEANING_FEE}
          />
        )}

        <BookingActions
          checkIn={checkInDate ?? undefined}
          checkOut={checkOutDate ?? undefined}
          guests={fixedGuests}
          propertyTitle={propertyTitle}
          airbnbUrl={airbnbUrl}
          property={property}
          price={price}
          maxGuests={property?.guests ?? maxGuests}
          onBookingClick={handleBookingClick}
        />
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onSubmit={handleCloseModal}
        closeModal={() => setIsModalOpen(false)}
        propertyTitle={propertyTitle}
        checkIn={checkInDate ?? undefined}
        checkOut={checkOutDate ?? undefined}
        nights={nights}
        totalPrice={totalPrice}
        guests={fixedGuests}
        property={property}
        maxGuests={property?.guests ?? maxGuests}
        price={price}
      />
    </>
  );
};

export default BookingCard;
